import type { Dispatch, SetStateAction } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

type BlogPostRef = { id: string; title?: string };

interface BlogPostDialogsProps {
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: Dispatch<SetStateAction<boolean>>;
  postToDelete: BlogPostRef | null;
  setPostToDelete: Dispatch<SetStateAction<BlogPostRef | null>>;
  handleDeletePost: () => void;
  showPublishDateDialog: boolean;
  setShowPublishDateDialog: Dispatch<SetStateAction<boolean>>;
  setPostToRepublish: Dispatch<SetStateAction<BlogPostRef | null>>;
  handleRepublishWithDate: (updateDate: boolean) => void;
  showPublishWarning: boolean;
  setShowPublishWarning: Dispatch<SetStateAction<boolean>>;
  setPostToPublish: Dispatch<SetStateAction<string | null>>;
  subscriberCount: number;
  confirmPublish: () => void;
}

const BlogPostDialogs = ({
  deleteDialogOpen,
  setDeleteDialogOpen,
  postToDelete,
  setPostToDelete,
  handleDeletePost,
  showPublishDateDialog,
  setShowPublishDateDialog,
  setPostToRepublish,
  handleRepublishWithDate,
  showPublishWarning,
  setShowPublishWarning,
  setPostToPublish,
  subscriberCount,
  confirmPublish,
}: BlogPostDialogsProps) => {
  return (
    <>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{postToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPostToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePost}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showPublishDateDialog} onOpenChange={setShowPublishDateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Publish Date?</AlertDialogTitle>
            <AlertDialogDescription>
              This post was previously published. Do you want to update the publish date to now?
              <br />
              <br />
              <strong>Keep Original Date:</strong> Post will be republished with its original date (no email
              notifications).
              <br />
              <br />
              <strong>Update to Now:</strong> Post will be republished with today's date and email notifications will
              be sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowPublishDateDialog(false);
                setPostToRepublish(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRepublishWithDate(false)}
              className="bg-secondary hover:bg-secondary/90"
            >
              Keep Original Date
            </AlertDialogAction>
            <AlertDialogAction onClick={() => handleRepublishWithDate(true)}>Update to Now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showPublishWarning} onOpenChange={setShowPublishWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish Blog Post?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send email notifications to <strong>{subscriberCount}</strong>
              {subscriberCount === 1 ? " subscriber" : " subscribers"}.
              {subscriberCount > 0 && (
                <>
                  <br />
                  <br />
                  You can view the notification log in Supabase after publishing.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowPublishWarning(false);
                setPostToPublish(null);
                setPostToRepublish(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmPublish}>Publish & Send Notifications</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BlogPostDialogs;
